import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { SscShellComponent } from '../ssc-shell/ssc-shell';

interface TranslationLanguage {
  id: number;
  language_code: string;
  language_name: string;
  native_name: string | null;
  script_name: string | null;
  direction: string;
}

interface ExamSubject {
  id: number;
  subject_code: string;
  subject_name: string;
  description: string | null;
}

interface TranslationQuality {
  confidence: string;
  adequacy: string;
  fluency: string;
  terminology: string;
  formatting: string;
}

@Component({
  selector: 'app-translation-workspace-page',
  standalone: true,
  imports: [CommonModule, FormsModule, SscShellComponent],
  templateUrl: './translation-workspace.html',
  styleUrl: './translation-workspace.scss',
})
export class TranslationWorkspacePage implements OnInit {
  private readonly formattedPasteBlockTags = new Set([
    'address',
    'article',
    'aside',
    'blockquote',
    'div',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'main',
    'nav',
    'p',
    'pre',
    'section',
  ]);

  protected languages: TranslationLanguage[] = [];
  protected subjects: ExamSubject[] = [];
  protected languagesLoading = true;
  protected subjectsLoading = true;
  protected sourceLanguage = '';
  protected targetLanguages: string[] = [];
  protected subject = '';
  protected sourceLanguageSearch = '';
  protected targetLanguageSearch = '';
  protected subjectSearch = '';
  protected sourceLanguagePanelOpen = false;
  protected targetLanguagePanelOpen = false;
  protected subjectPanelOpen = false;
  protected questionText = 'Find the value of x if 2x + 5 = 17. Choose the correct answer: (A) 4 (B) 5 (C) 6 (D) 7';
  protected translatedText = 'यदि 2x + 5 = 17 है, तो x का मान ज्ञात कीजिए। सही उत्तर चुनें: (A) 4 (B) 5 (C) 6 (D) 7';
  protected attachedImage: File | null = null;
  protected attachedImagePreview = '';
  protected extractedImageText = '';
  protected translateLoading = false;
  protected translateError = '';
  protected translationQuality: TranslationQuality = this.emptyQuality();

  constructor(
    private readonly httpSvc: Httpservice,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadLanguages();
    this.loadSubjects();
  }

  protected get selectedSourceLanguage(): TranslationLanguage | null {
    return this.languages.find((language) => language.language_code === this.sourceLanguage) ?? null;
  }

  protected get selectedTargetLanguage(): TranslationLanguage | null {
    return this.selectedTargetLanguages[0] ?? null;
  }

  protected get selectedTargetLanguages(): TranslationLanguage[] {
    return this.targetLanguages
      .map((code) => this.languages.find((language) => language.language_code === code) ?? null)
      .filter((language): language is TranslationLanguage => Boolean(language));
  }

  protected get languagePairLabel(): string {
    const source = this.selectedSourceLanguage?.language_name ?? 'Auto-detect';
    const target = this.selectedTargetLanguages.length
      ? this.selectedTargetLanguages.map((language) => language.language_name).join(', ')
      : 'Target';
    return `${source} to ${target}`;
  }

  protected get selectedSubject(): ExamSubject | null {
    return this.subjects.find((item) => item.subject_name === this.subject) ?? null;
  }

  protected get targetLanguageNames(): string {
    return this.selectedTargetLanguages.length ? this.selectedTargetLanguages.map((language) => language.language_name).join(', ') : '-';
  }

  protected get targetLanguageScripts(): string {
    return this.selectedTargetLanguages.length ? this.selectedTargetLanguages.map((language) => language.script_name || '-').join(', ') : '-';
  }

  protected get targetLanguageDirections(): string {
    return this.selectedTargetLanguages.length ? this.selectedTargetLanguages.map((language) => language.direction || '-').join(', ') : '-';
  }

  protected get filteredSourceLanguages(): TranslationLanguage[] {
    return this.filterLanguages(this.sourceLanguageSearch);
  }

  protected get filteredTargetLanguages(): TranslationLanguage[] {
    return this.filterLanguages(this.targetLanguageSearch).filter((language) => !this.targetLanguages.includes(language.language_code));
  }

  protected get filteredSubjects(): ExamSubject[] {
    const searchTerm = this.normalizeSearch(this.subjectSearch);
    if (!searchTerm) {
      return this.subjects;
    }

    return this.subjects.filter((item) => this.normalizeSearch(`${item.subject_name} ${item.subject_code}`).includes(searchTerm));
  }

  protected onSourceLanguageSearch(value: string): void {
    this.sourceLanguageSearch = value;
    if (!value.trim()) {
      this.sourceLanguage = '';
    }
    this.sourceLanguagePanelOpen = true;
  }

  protected onTargetLanguageSearch(value: string): void {
    this.targetLanguageSearch = value;
    this.targetLanguagePanelOpen = true;
  }

  protected onSubjectSearch(value: string): void {
    this.subjectSearch = value;
    if (!value.trim()) {
      this.subject = '';
    }
    this.subjectPanelOpen = true;
  }

  protected selectSourceLanguage(language: TranslationLanguage): void {
    this.sourceLanguage = language.language_code;
    this.sourceLanguageSearch = this.getLanguageLabel(language);
    this.sourceLanguagePanelOpen = false;
  }

  protected selectTargetLanguage(language: TranslationLanguage): void {
    if (!this.targetLanguages.includes(language.language_code)) {
      this.targetLanguages = [...this.targetLanguages, language.language_code];
    }
    this.targetLanguageSearch = '';
    this.targetLanguagePanelOpen = false;
  }

  protected removeTargetLanguage(languageCode: string): void {
    this.targetLanguages = this.targetLanguages.filter((code) => code !== languageCode);
  }

  protected selectSubject(item: ExamSubject): void {
    this.subject = item.subject_name;
    this.subjectSearch = item.subject_name;
    this.subjectPanelOpen = false;
  }

  protected closeAutocompletePanel(panel: 'source' | 'target' | 'subject'): void {
    window.setTimeout(() => {
      if (panel === 'source') {
        this.sourceLanguagePanelOpen = false;
        this.sourceLanguageSearch = this.selectedSourceLanguage ? this.getLanguageLabel(this.selectedSourceLanguage) : '';
      }

      if (panel === 'target') {
        this.targetLanguagePanelOpen = false;
        this.targetLanguageSearch = '';
      }

      if (panel === 'subject') {
        this.subjectPanelOpen = false;
        this.subjectSearch = this.selectedSubject?.subject_name ?? '';
      }

      this.cdr.markForCheck();
    }, 160);
  }

  protected translateQuestion(): void {
    if (this.translateLoading || (!this.questionText.trim() && !this.attachedImage)) {
      return;
    }

    this.translateLoading = true;
    this.translateError = '';

    const targets = this.selectedTargetLanguages.length ? this.selectedTargetLanguages : [];
    if (!targets.length) {
      this.translateError = 'Please select at least one target language.';
      this.translateLoading = false;
      return;
    }

    const requests = this.attachedImage
      ? targets.map((target) => this.httpSvc.requestCall(AuthEndPoints.TRANSLATE_WORKSPACE_IMAGE, ApiMethod.POST, this.buildImageTranslatePayload(target), false, { suppressToast: true }))
      : targets.map((target) => this.httpSvc.requestCall(AuthEndPoints.TRANSLATE_WORKSPACE_QUESTION, ApiMethod.POST, this.buildTranslatePayload(target), false, { suppressToast: true }));

    forkJoin(requests).subscribe({
        next: (responses) => {
          this.applyTranslationResponses(responses, targets);
          this.translateLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.translateError = this.resolveTranslateError(error);
          this.translateLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected preserveQuestionPaste(event: ClipboardEvent): void {
    const textarea = event.target as HTMLTextAreaElement | null;
    const imageFile = this.getPastedImage(event);
    if (imageFile) {
      event.preventDefault();
      this.setAttachedImage(imageFile);
      return;
    }

    const clipboardText = this.getFormattedClipboardText(event);
    if (!textarea || !clipboardText) {
      return;
    }

    event.preventDefault();
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const nextValue = `${this.questionText.slice(0, selectionStart)}${clipboardText}${this.questionText.slice(selectionEnd)}`;
    this.questionText = nextValue;

    window.setTimeout(() => {
      const cursorPosition = selectionStart + clipboardText.length;
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    });
  }

  protected onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (file) {
      this.setAttachedImage(file);
    }
    input.value = '';
  }

  protected removeAttachedImage(): void {
    if (this.attachedImagePreview) {
      URL.revokeObjectURL(this.attachedImagePreview);
    }
    this.attachedImage = null;
    this.attachedImagePreview = '';
    this.extractedImageText = '';
  }

  private getFormattedClipboardText(event: ClipboardEvent): string {
    const htmlText = event.clipboardData?.getData('text/html') ?? '';
    if (htmlText.trim()) {
      const formattedText = this.convertHtmlToFormattedText(htmlText);
      if (formattedText.trim()) {
        return formattedText;
      }
    }

    return event.clipboardData?.getData('text/plain') ?? '';
  }

  private getPastedImage(event: ClipboardEvent): File | null {
    const items = Array.from(event.clipboardData?.items ?? []);
    const imageItem = items.find((item) => item.type.startsWith('image/'));
    return imageItem?.getAsFile() ?? null;
  }

  private setAttachedImage(file: File): void {
    if (!file.type.startsWith('image/')) {
      this.translateError = 'Please attach a valid image file.';
      return;
    }
    if (this.attachedImagePreview) {
      URL.revokeObjectURL(this.attachedImagePreview);
    }
    this.attachedImage = file;
    this.attachedImagePreview = URL.createObjectURL(file);
    this.extractedImageText = '';
    this.translateError = '';
  }

  private convertHtmlToFormattedText(htmlText: string): string {
    const body = new DOMParser().parseFromString(htmlText, 'text/html').body;
    return this.normalizeFormattedText(this.nodeToFormattedText(body));
  }

  private nodeToFormattedText(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent?.replace(/\u00a0/g, ' ') ?? '';
    }

    if (!(node instanceof HTMLElement)) {
      return Array.from(node.childNodes).map((child) => this.nodeToFormattedText(child)).join('');
    }

    const tagName = node.tagName.toLowerCase();
    if (tagName === 'br') {
      return '\n';
    }

    if (tagName === 'li') {
      return `\n- ${this.childNodesToFormattedText(node).trim()}`;
    }

    if (tagName === 'tr') {
      return `${this.childNodesToFormattedText(node).trimEnd()}\n`;
    }

    if (tagName === 'td' || tagName === 'th') {
      return `${this.childNodesToFormattedText(node).trim()}\t`;
    }

    const childText = this.childNodesToFormattedText(node);
    if (this.formattedPasteBlockTags.has(tagName)) {
      return `\n${childText.trim()}\n`;
    }

    return childText;
  }

  private childNodesToFormattedText(node: Node): string {
    return Array.from(node.childNodes).map((child) => this.nodeToFormattedText(child)).join('');
  }

  private normalizeFormattedText(value: string): string {
    return value
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\t+\n/g, '\n')
      .replace(/\n{2,}/g, '\n')
      .trim();
  }

  private loadSubjects(): void {
    this.subjectsLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.GET_TRANSLATION_WORKSPACE_SUBJECTS, ApiMethod.GET, '', false, { suppressToast: true })
      .subscribe({
        next: (response) => {
          const rows = this.extractSubjectRows(response);
          this.subjects = rows.map((row) => this.mapSubject(row));
          this.syncSelectedSubject();
          this.subjectsLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.subjects = [];
          this.syncSelectedSubject();
          this.subjectsLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadLanguages(): void {
    this.languagesLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.GET_INDIAN_LANGUAGES, ApiMethod.GET, '', false, { suppressToast: true })
      .subscribe({
        next: (response) => {
          const rows = this.extractLanguageRows(response);
          this.languages = rows.map((row) => this.mapLanguage(row));
          this.syncSelectedLanguages();
          this.languagesLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.languages = [];
          this.syncSelectedLanguages();
          this.languagesLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private extractLanguageRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(this.isLanguageRecord);
    }

    if (!this.isPlainObject(response)) {
      return [];
    }

    const value = response['value'];
    if (this.isPlainObject(value) && Array.isArray(value['languages'])) {
      return value['languages'].filter(this.isLanguageRecord);
    }

    if (Array.isArray(response['languages'])) {
      return response['languages'].filter(this.isLanguageRecord);
    }

    if (Array.isArray(value)) {
      return value.filter(this.isLanguageRecord);
    }

    return [];
  }

  private extractSubjectRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(this.isPlainObject);
    }

    if (!this.isPlainObject(response)) {
      return [];
    }

    const value = response['value'];
    if (this.isPlainObject(value) && Array.isArray(value['subjects'])) {
      return value['subjects'].filter(this.isPlainObject);
    }

    if (Array.isArray(response['subjects'])) {
      return response['subjects'].filter(this.isPlainObject);
    }

    if (Array.isArray(value)) {
      return value.filter(this.isPlainObject);
    }

    return [];
  }

  private syncSelectedLanguages(): void {
    if (!this.languages.length) {
      this.sourceLanguage = '';
      this.targetLanguages = [];
      return;
    }

    if (!this.languages.some((language) => language.language_code === this.sourceLanguage)) {
      this.sourceLanguage = '';
    }

    this.targetLanguages = this.targetLanguages.filter((code) => this.languages.some((language) => language.language_code === code));

    if (!this.targetLanguages.length) {
      const hindiLanguage = this.languages.find((language) => language.language_code === 'hi');
      const nonSourceLanguage = this.languages.find((language) => language.language_code !== this.sourceLanguage);
      const fallbackCode = hindiLanguage?.language_code ?? nonSourceLanguage?.language_code ?? this.sourceLanguage;
      this.targetLanguages = fallbackCode ? [fallbackCode] : [];
    }

    this.sourceLanguageSearch = this.selectedSourceLanguage ? this.getLanguageLabel(this.selectedSourceLanguage) : '';
    this.targetLanguageSearch = '';
  }

  private syncSelectedSubject(): void {
    if (!this.subjects.length) {
      this.subject = '';
      return;
    }

    if (!this.subjects.some((item) => item.subject_name === this.subject)) {
      this.subject = '';
    }

    this.subjectSearch = this.selectedSubject?.subject_name ?? '';
  }

  private mapLanguage(row: Record<string, unknown>): TranslationLanguage {
    return {
      id: Number(row['id']),
      language_code: String(row['language_code'] ?? ''),
      language_name: String(row['language_name'] ?? ''),
      native_name: typeof row['native_name'] === 'string' ? row['native_name'] : null,
      script_name: typeof row['script_name'] === 'string' ? row['script_name'] : null,
      direction: typeof row['direction'] === 'string' ? row['direction'] : 'LTR',
    };
  }

  private mapSubject(row: Record<string, unknown>): ExamSubject {
    return {
      id: Number(row['id']),
      subject_code: String(row['subject_code'] ?? ''),
      subject_name: String(row['subject_name'] ?? ''),
      description: typeof row['description'] === 'string' ? row['description'] : null,
    };
  }

  private filterLanguages(searchValue: string): TranslationLanguage[] {
    const searchTerm = this.normalizeSearch(searchValue);
    if (!searchTerm) {
      return this.languages;
    }

    return this.languages.filter((language) => this.normalizeSearch(this.getLanguageLabel(language)).includes(searchTerm));
  }

  private getLanguageLabel(language: TranslationLanguage): string {
    return `${language.language_name}${language.native_name ? ` - ${language.native_name}` : ''}`;
  }

  private normalizeSearch(value: string): string {
    return value.trim().toLowerCase();
  }

  private buildTranslatePayload(targetLanguage: TranslationLanguage) {
    return {
      question: this.questionText.trim(),
      source_language: this.selectedSourceLanguage?.language_name ?? '',
      target_language: targetLanguage.language_name,
      subject: this.selectedSubject?.subject_name ?? '',
    };
  }

  private buildImageTranslatePayload(targetLanguage: TranslationLanguage): FormData {
    const formData = new FormData();
    if (this.attachedImage) {
      formData.append('image', this.attachedImage);
    }
    formData.append('source_language', this.selectedSourceLanguage?.language_name ?? '');
    formData.append('target_language', targetLanguage.language_name);
    formData.append('subject', this.selectedSubject?.subject_name ?? '');
    return formData;
  }

  private applyTranslationResponses(responses: unknown[], targets: TranslationLanguage[]): void {
    const translatedItems = responses.map((response, index) => {
      const translatedText = this.extractTranslatedText(response);
      return {
        languageName: targets[index]?.language_name ?? 'Target',
        translatedText,
      };
    });
    this.translatedText = translatedItems.map((item) => `[${item.languageName}]\n${item.translatedText.outputText}`).join('\n\n');
    this.translationQuality = translatedItems[0]?.translatedText.quality ?? this.emptyQuality();
    this.extractedImageText = this.extractImageText(responses[0]);
  }

  private extractTranslatedText(response: unknown): { outputText: string; quality: TranslationQuality } {
    if (!this.isPlainObject(response)) {
      return { outputText: this.translatedText, quality: this.emptyQuality() };
    }

    const value = response['value'];
    if (!this.isPlainObject(value)) {
      return { outputText: this.translatedText, quality: this.emptyQuality() };
    }

    const translatedText = value['translated_text'];
    if (typeof translatedText !== 'string' || !translatedText.trim()) {
      return { outputText: this.translatedText, quality: this.emptyQuality() };
    }

    return this.parseLlmTranslation(translatedText);
  }

  private extractImageText(response: unknown): string {
    if (!this.isPlainObject(response)) {
      return this.extractedImageText;
    }
    const value = response['value'];
    if (!this.isPlainObject(value)) {
      return this.extractedImageText;
    }
    return typeof value['extracted_text'] === 'string' ? value['extracted_text'] : this.extractedImageText;
  }

  private parseLlmTranslation(value: string): { outputText: string; quality: TranslationQuality } {
    const normalizedValue = this.normalizeLlmJson(value);
    try {
      const parsed = JSON.parse(normalizedValue) as Record<string, unknown>;
      return this.buildTranslationDisplay(parsed, value);
    } catch {
      return this.parseLooseLlmTranslation(value);
    }
  }

  private normalizeLlmJson(value: string): string {
    const trimmedValue = value.trim();
    const withoutFence = trimmedValue
      .replace(/^`{1,3}\s*(?:json)?\s*/i, '')
      .replace(/\s*`{1,3}$/i, '')
      .trim();
    const jsonStart = withoutFence.indexOf('{');
    const jsonEnd = withoutFence.lastIndexOf('}');

    if (jsonStart >= 0 && jsonEnd > jsonStart) {
      return withoutFence.slice(jsonStart, jsonEnd + 1);
    }

    return withoutFence;
  }

  private parseLooseLlmTranslation(value: string): { outputText: string; quality: TranslationQuality } {
    const question = this.extractStringField(value, 'translated_question');
    const options = this.extractOptionsText(value);
    const quality = this.mapQuality({
      confidence: this.extractLooseField(value, 'confidence'),
      adequacy: this.extractLooseField(value, 'adequacy'),
      fluency: this.extractLooseField(value, 'fluency'),
      terminology: this.extractStringField(value, 'terminology'),
      formatting: this.extractStringField(value, 'formatting'),
    });
    const outputText = this.buildOutputText(question, options);

    return {
      outputText: outputText || 'Translation response did not include translated text.',
      quality,
    };
  }

  private buildTranslationDisplay(parsed: Record<string, unknown>, fallbackValue: string): { outputText: string; quality: TranslationQuality } {
    const question = typeof parsed['translated_question'] === 'string' ? parsed['translated_question'] : '';
    const options = this.formatOptions(parsed['translated_options']);
    const outputText = this.buildOutputText(question, options);

    return {
      outputText: outputText || fallbackValue,
      quality: this.mapQuality(parsed),
    };
  }

  private buildOutputText(question: string, options: string): string {
    const normalizedQuestion = this.stripDuplicatedOptionsFromQuestion(question, options);
    return [normalizedQuestion, options].filter(Boolean).join('\n\n');
  }

  private stripDuplicatedOptionsFromQuestion(question: string, options: string): string {
    const normalizedQuestion = this.normalizeTranslatedQuestion(question);
    if (!options.trim()) {
      return normalizedQuestion;
    }

    const lines = normalizedQuestion
      .split('\n')
      .map((line) => line.trimEnd())
      .filter((line) => !this.isOptionLine(line));
    const withoutTrailingInlineOptions = lines.join('\n').replace(/\s*(?:\([A-D]\)|[A-D][).])\s*[\s\S]*$/u, '').trim();

    return withoutTrailingInlineOptions || normalizedQuestion;
  }

  private normalizeTranslatedQuestion(question: string): string {
    return question
      .replace(/\\\s*\n/g, '\n')
      .replace(/\\n/g, '\n')
      .trim();
  }

  private isOptionLine(line: string): boolean {
    return /^\s*(?:\([A-D]\)|[A-D][).])\s+/u.test(line);
  }

  private formatOptions(options: unknown): string {
    if (typeof options === 'string') {
      return this.normalizeOptionText(options);
    }

    if (!this.isPlainObject(options)) {
      return '';
    }

    return ['A', 'B', 'C', 'D']
      .map((key) => {
        const option = options[key];
        return typeof option === 'string' && option.trim() ? `(${key}) ${option.trim()}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }

  private normalizeOptionText(value: string): string {
    return value
      .replace(/\\n/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n');
  }

  private extractOptionsText(value: string): string {
    const objectMatch = value.match(/"translated_options"\s*:\s*\{([\s\S]*?)\}\s*,\s*"confidence"/);
    if (objectMatch?.[1]) {
      const optionRows = ['A', 'B', 'C', 'D']
        .map((key) => {
          const option = this.extractStringField(`{${objectMatch[1]}}`, key);
          return option ? `(${key}) ${option}` : '';
        })
        .filter(Boolean);
      return optionRows.join('\n');
    }

    const textOptions = this.extractStringField(value, 'translated_options');
    return textOptions ? this.normalizeOptionText(textOptions) : '';
  }

  private extractStringField(value: string, fieldName: string): string {
    const pattern = new RegExp(`"${fieldName}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 'i');
    const match = value.match(pattern);
    if (!match?.[1]) {
      return '';
    }

    return this.decodeJsonString(match[1]);
  }

  private extractLooseField(value: string, fieldName: string): string {
    const quotedValue = this.extractStringField(value, fieldName);
    if (quotedValue) {
      return quotedValue;
    }

    const pattern = new RegExp(`"${fieldName}"\\s*:\\s*([0-9.]+)`, 'i');
    return value.match(pattern)?.[1] ?? '';
  }

  private decodeJsonString(value: string): string {
    try {
      return JSON.parse(`"${value}"`);
    } catch {
      return value.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    }
  }

  private resolveTranslateError(error: unknown): string {
    if (this.isPlainObject(error)) {
      const errorBody = error['error'];
      if (this.isPlainObject(errorBody) && typeof errorBody['msg'] === 'string') {
        return errorBody['msg'];
      }
    }

    return 'Unable to translate question. Please try again.';
  }

  private mapQuality(parsed: Record<string, unknown>): TranslationQuality {
    return {
      confidence: this.formatMetric(parsed['confidence'], '%'),
      adequacy: this.formatScoreMetric(parsed['adequacy']),
      fluency: this.formatScoreMetric(parsed['fluency']),
      terminology: this.formatTextMetric(parsed['terminology']),
      formatting: this.formatTextMetric(parsed['formatting']),
    };
  }

  private formatMetric(value: unknown, suffix: string): string {
    if (typeof value === 'number') {
      return `${value}${suffix}`;
    }

    if (typeof value === 'string' && value.trim()) {
      return value.includes(suffix.trim()) ? value : `${value}${suffix}`;
    }

    return '-';
  }

  private formatScoreMetric(value: unknown): string {
    const numericValue = this.toMetricNumber(value);
    if (numericValue === null) {
      return typeof value === 'string' && value.trim() ? value : '-';
    }

    if (numericValue <= 100) {
      const normalizedValue = numericValue <= 5 ? numericValue : numericValue / 20;
      return `${this.trimMetricDecimal(normalizedValue)} / 5`;
    }

    return String(numericValue);
  }

  private trimMetricDecimal(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
  }

  private toMetricNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim().replace('%', '').split('/')[0]?.trim();
    if (!normalizedValue) {
      return null;
    }

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private formatTextMetric(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) {
      return '-';
    }

    return this.shortenQualityText(value);
  }

  private shortenQualityText(value: string): string {
    const cleanedValue = value
      .replace(/\s+/g, ' ')
      .replace(/\.$/, '')
      .trim();
    const commaParts = cleanedValue.split(',').map((part) => part.trim()).filter(Boolean);

    if (commaParts.length > 1) {
      return commaParts.slice(0, 3).join(', ');
    }

    return cleanedValue.split(' ').slice(0, 5).join(' ');
  }

  private emptyQuality(): TranslationQuality {
    return {
      confidence: '-',
      adequacy: '-',
      fluency: '-',
      terminology: '-',
      formatting: '-',
    };
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private isLanguageRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
